import { useEffect, useMemo, useState } from "react";
import { css, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { AicoConnection, PageHeader, TableLayout, NewButton, ReactiveTable, LoadingSpinner } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from "../../../../../Definitions";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from "@tanstack/react-table";
import { useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { LanControllersDeviceChangeQueueResponse, LanControllersDeviceChangeQueueArgs, LAN_CONTROLLERS_DEVICE_CHANGE_QUEUE } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/deviceChangeQueue";
import { LC_TABLE_IDS, TABLE_ID_TO_NAME } from "../../../../../Definitions/lcTableIds";
import { LimitOffsetPaginationMeta } from "../../../../../Services/Agrisys_GqlDataCaller/sharedTypes";
import { useTranslation } from "react-i18next";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "timestamp", accessor: "timestamp", type: ListData_ValueTypes.Date, sort: true },
	{ headerValue: "table", accessor: "tableName", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "operation type", accessor: "operationType", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "row id", accessor: "row_id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "hardware id", accessor: "hardwareId", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "error", accessor: "error", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "lc id", accessor: "fk_lan_controllers", type: ListData_ValueTypes.Number, sort: true },
]

enum DeviceQueueOperation {
	set = 1,
	add = 2,
	update = 3,
	delete = 4,
	reset = 5,
	clearAllTables = 6,
	addRowItem = 7,
	updateRowItem = 8,
	deleteRowItem = 9,
}

const DeviceQueueOperationNames: Record<DeviceQueueOperation, string> = {
	[DeviceQueueOperation.set]: "Set table",
	[DeviceQueueOperation.add]: "Add row",
	[DeviceQueueOperation.update]: "Update row",
	[DeviceQueueOperation.delete]: "Delete row",
	[DeviceQueueOperation.reset]: "Reset",
	[DeviceQueueOperation.clearAllTables]: "Clear all tables",
	[DeviceQueueOperation.addRowItem]: "Add row item",
	[DeviceQueueOperation.updateRowItem]: "Update row item",
	[DeviceQueueOperation.deleteRowItem]: "Delete row item",
};

enum LC_PROTOCOL_ERRORS {
	NACK = 2,
	RESEND = 3,
	UNDEFINED = 4,
	ERROR = 5,
	FAILED_TO_SET_ALL_ROWS = 10,

	TIMEOUT = 1000,
	UNKNOWN = 1001,
}

const LC_PROTOCOL_ERROR_MESSAGE: Record<LC_PROTOCOL_ERRORS, string> = {
	[LC_PROTOCOL_ERRORS.NACK]: "NACK",
	[LC_PROTOCOL_ERRORS.RESEND]: "Resend requested",
	[LC_PROTOCOL_ERRORS.UNDEFINED]: "Undefined error",
	[LC_PROTOCOL_ERRORS.ERROR]: "Error",
	[LC_PROTOCOL_ERRORS.FAILED_TO_SET_ALL_ROWS]: "Failed to set all rows",

	[LC_PROTOCOL_ERRORS.TIMEOUT]: "Timeout",
	[LC_PROTOCOL_ERRORS.UNKNOWN]: "Unknown error",
};

export default function DeviceChangeQueue() {
	const { t } = useTranslation();
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
	const limit = 50;

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: limit,
	});

	const [pageMeta, setPageMeta] = useState<LimitOffsetPaginationMeta | null>(null);

	const { state: { deviceConnection } } = useAicoConnection();

	const [fetchData, { data: data, loading: loading, error: error, }] = useLazyQuery<LanControllersDeviceChangeQueueResponse, LanControllersDeviceChangeQueueArgs>(LAN_CONTROLLERS_DEVICE_CHANGE_QUEUE, {
		fetchPolicy: "network-only",
	});

	useEffect(() => {
		if (deviceConnection) {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData({
				variables: {
					domain: deviceConnection.domain,
					input: {
						ids: [deviceConnection.lanControllerId],
					},
					pagination: {
						limit,
						offset: pagination.pageIndex * limit,
					}
				}
			})
		} else {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData(); //TODO: find a better way to do this, as this will always give an err?
		}
	}, [deviceConnection]);

	const tableData = useMemo(() => {
		const lcData = data?.controller.lanControllersByIds?.[0];
		if (lcData) {
			const paginationInfo: LimitOffsetPaginationMeta = lcData.device_change_queue.meta;
			setPageMeta(paginationInfo);

			return lcData.device_change_queue.data.map(e => {
				let tableName = 'unknown';
				if (e.fk_table_definitions in LC_TABLE_IDS) {
					const tableType: LC_TABLE_IDS = e.fk_table_definitions;
					tableName = TABLE_ID_TO_NAME[tableType];
				}

				let operationTypeName = 'unknown';
				if (e.fk_operation_types in DeviceQueueOperation) {
					const operationType: DeviceQueueOperation = e.fk_operation_types;
					operationTypeName = DeviceQueueOperationNames[operationType];
				}

				let error: string | undefined;
				if (e.message_type) {
					if (e.message_type in LC_PROTOCOL_ERRORS) {
						const errorType: LC_PROTOCOL_ERRORS = e.message_type;
						error = LC_PROTOCOL_ERROR_MESSAGE[errorType];
					} else {
						error = LC_PROTOCOL_ERROR_MESSAGE[LC_PROTOCOL_ERRORS.TIMEOUT];
					}
				}

				return {
					...e,
					hw_id: undefined,
					id: e.device_change_queue_id,
					timestamp: e.timestamp * 1000,
					tableName: tableName,
					operationType: operationTypeName,
					hardwareId: e.hw_id ? e.hw_id.join(",") : undefined,
					error: error
				}
			})
		}

		return [];
	}, [data]);

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onPaginationChange: setPagination,
		state: {
			pagination,
		},
		manualPagination: true,
		pageCount: pageMeta
			? Math.ceil(pageMeta.totalCount / limit)
			: -1,
	});

	return (
		<frames.Page_inner>
			<PageHeader topHeader='Device Data' subHeader='Queue' />
			<frames._page_inner_content>
				<TableLayout
					fillScreenTable
					searchbBarArea={mobileXs ? undefined : <AicoConnection inTable />}
					buttonArea={
						<div style={{ display: "flex", gap: "5px" }}>
							{mobileXs && <AicoConnection inTable />}
							<NewButton
								content={"fetch"}
								buttonType={ButtonTypes.tableButton}
								startEnhancer={glyphs.Fetch({})}
								onClick={() => {
									if (deviceConnection) {
										fetchData({
											variables: {
												domain: deviceConnection.domain,
												input: {
													ids: [deviceConnection.lanControllerId],
												},
												pagination: {
													limit: limit,
													offset: pagination.pageIndex * limit,
												},
											},
										});
									}
								}}
								disabled={loading || !deviceConnection}
							/>
						</div>
					}
					tableArea={<>
						{loading && <div style={{ padding: "20px", display: "flex", justifyContent: "center" }}><LoadingSpinner /></div>}
						{(!loading && (error || tableData.length === 0)) && <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("noData", { defaultValue: "no data" })}</p>}
						{!loading && !error && tableData.length !== 0 &&
							<ReactiveTable
								table={table}
								tableType={TableStyles_Types.primaryTable}
								emptyText="dut"
								renderFooter={false}
								loading={loading}
								minRows={30}
							/>
						}
					</>}
					paginationBtns={{
						onPrev: () => {
							if (pageMeta?.hasPreviousPage && deviceConnection) {
								const newIndex = pagination.pageIndex - 1;

								fetchData({
									variables: {
										domain: deviceConnection.domain,
										input: { ids: [deviceConnection.lanControllerId] },
										pagination: {
											limit,
											offset: newIndex * limit,
										},
									},
								});

								setPagination(p => ({ ...p, pageIndex: newIndex }));
							}
						},

						onNext: () => {
							if (pageMeta?.hasNextPage && deviceConnection) {
								const newIndex = pagination.pageIndex + 1;

								fetchData({
									variables: {
										domain: deviceConnection.domain,
										input: { ids: [deviceConnection.lanControllerId] },
										pagination: {
											limit,
											offset: newIndex * limit,
										},
									},
								});

								setPagination(p => ({ ...p, pageIndex: newIndex }));
							}
						},

						canNextPage: !pageMeta?.hasNextPage,
						canPreviousPage: pagination.pageIndex === 0 || !pageMeta?.hasPreviousPage,

						thisPageIndex: pagination.pageIndex + 1,
						pageCount: pageMeta
							? Math.ceil(pageMeta.totalCount / limit)
							: 0,
					}}
				/>

			</frames._page_inner_content>
		</frames.Page_inner>
	)
}